import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaComments, FaCopy, FaCheck, FaHandshake } from 'react-icons/fa';

import { founderTypes } from '../data/founderTypes';
import { getCofounderPrompts } from '../utils/profileDimensions';
import { trackEvent } from '../utils/analytics';

const Container = styled(motion.section)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.92) 0%,
    rgba(37, 65, 178, 0.06) 100%
  );
  border-radius: var(--border-radius);
  padding: 2.5rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(37, 65, 178, 0.2);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--secondary-color), var(--primary-color));
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.75rem;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const Description = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  max-width: 620px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  gap: 1.25rem;
`;

const ScriptCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.5rem;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const ScriptHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const ScriptTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0;
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary-color);
  background: rgba(var(--primary-rgb), 0.1);
  border: 1px solid rgba(var(--primary-rgb), 0.25);
  border-radius: 8px;
  padding: 0.4rem 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--primary-rgb), 0.18);
  }
`;

const ScriptBody = styled.blockquote`
  margin: 0;
  padding-left: 1rem;
  border-left: 3px solid var(--primary-color);
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 1.05rem;
  font-style: italic;
`;

const SubsectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  font-size: 1.15rem;
  font-weight: 600;
  margin: 2rem 0 1rem;
`;

const CopyableRow = ({ title, text, trackLabel }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      trackEvent('script_copied', { title: trackLabel || title });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback ignored
    }
  };

  return (
    <ScriptCard>
      <ScriptHeader>
        <ScriptTitle>{title}</ScriptTitle>
        <CopyButton type="button" onClick={handleCopy} aria-label={`Copy ${title}`}>
          {copied ? <FaCheck aria-hidden="true" /> : <FaCopy aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy'}
        </CopyButton>
      </ScriptHeader>
      <ScriptBody>{text}</ScriptBody>
    </ScriptCard>
  );
};

const MeetingScriptsSection = ({ playbook, profile }) => {
  const scripts = playbook?.meetingScripts || [];
  const cofounderPrompts = getCofounderPrompts(
    {
      primaryType: profile?.primaryType,
      compatibleTypes: profile?.compatibleTypes || [],
    },
    founderTypes
  );

  if (scripts.length === 0 && cofounderPrompts.length === 0) {
    return null;
  }

  return (
    <Container
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      aria-labelledby="meeting-scripts-heading"
    >
      <Header>
        <Eyebrow>
          <FaComments aria-hidden="true" />
          Ready to use
        </Eyebrow>
        <Title id="meeting-scripts-heading">Meeting scripts & co-founder prompts</Title>
        <Description>
          Copy these word-for-word into your next standup, co-founder sync, or hard conversation.
          Built for how you actually operate under pressure.
        </Description>
      </Header>

      {scripts.length > 0 && (
        <>
          <SubsectionTitle>Meeting scripts</SubsectionTitle>
          <Grid>
            {scripts.map((item) => (
              <CopyableRow
                key={item.title}
                title={item.title}
                text={item.script}
                trackLabel={`meeting_${item.title}`}
              />
            ))}
          </Grid>
        </>
      )}

      {cofounderPrompts.length > 0 && (
        <>
          <SubsectionTitle>
            <FaHandshake aria-hidden="true" />
            Co-founder conversation starters
          </SubsectionTitle>
          <Grid>
            {cofounderPrompts.map((item) => (
              <CopyableRow
                key={item.title}
                title={item.title}
                text={item.prompt}
                trackLabel={`cofounder_${item.title}`}
              />
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
};

export default MeetingScriptsSection;
